import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/firebase";
import {
  Building2,
  Plus,
  Search,
  RefreshCw,
  Edit3,
  Trash2,
  X,
  Loader2,
  Users,
  Upload,
  CheckCircle2,
  AlertTriangle,
  FileText,
  ChevronRight,
  UserPlus,
  ShieldCheck,
  ShieldOff,
  ShieldAlert,
  Stethoscope,
  BarChart3,
} from "lucide-react";

// ─── Constants ─────────────────────────────────────────────
// B2C is NOT a company — it's an individual user with no clientId.
// Companies (clients) can only be B2B (private sector) or B2G (government).
const CATEGORIES = [
  { key: "B2B", label: "B2B", color: "#0284C7", bg: "#E0F2FE" },
  { key: "B2G", label: "B2G", color: "#9333EA", bg: "#F3E8FF" },
];

const COUNTRIES = [
  { code: "PA", name: "Panamá" },
  { code: "CO", name: "Colombia" },
  { code: "MX", name: "México" },
  { code: "US", name: "Estados Unidos" },
  { code: "OTHER", name: "Otro" },
];

function categoryStyle(cat) {
  return CATEGORIES.find((c) => c.key === cat) || { color: "#64748B", bg: "#F1F5F9", label: cat || "—" };
}

function fmtDate(v) {
  if (!v) return "—";
  if (typeof v === "string") return new Date(v).toLocaleDateString("es-CO");
  if (v.toDate) return v.toDate().toLocaleDateString("es-CO");
  if (v.seconds) return new Date(v.seconds * 1000).toLocaleDateString("es-CO");
  return "—";
}

// ─── CSV parsing helpers ───────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Dominios de email genéricos/personales que NO se permiten como dominio
 * corporativo de un cliente. El admin del cliente solo puede ver/gestionar
 * usuarios con email de su propio dominio corporativo.
 */
const GENERIC_DOMAINS = new Set([
  "gmail.com", "googlemail.com",
  "outlook.com", "hotmail.com", "hotmail.es", "hotmail.co", "live.com",
  "yahoo.com", "yahoo.es", "yahoo.co", "ymail.com",
  "icloud.com", "me.com", "mac.com",
  "aol.com",
  "protonmail.com", "proton.me",
  "zoho.com",
  "mail.com",
  "gmx.com", "gmx.net",
  "msn.com",
  "inbox.com",
]);

/**
 * Valida que un dominio no sea genérico/personal.
 * @param {string} domain - ej. "empresa.com"
 * @returns {boolean}
 */
function isCorporateDomain(domain) {
  return !GENERIC_DOMAINS.has(domain.toLowerCase().replace(/^@/, "").trim());
}

function parseCsv(text) {
  // Detect delimiter (comma, semicolon, tab)
  const firstLine = (text.split(/\r?\n/)[0] || "").trim();
  const delim = firstLine.includes(";") ? ";" : firstLine.includes("\t") ? "\t" : ",";

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return { rows: [], headerDetected: false };

  // Detect header by looking for "email" keyword
  const firstFields = lines[0].split(delim).map((s) => s.trim().toLowerCase());
  const headerDetected = firstFields.some((f) => f === "email" || f === "correo");

  let header = ["email", "nombre", "apellido"];
  let dataLines = lines;
  if (headerDetected) {
    header = firstFields.map((h) => {
      if (h === "correo") return "email";
      if (h === "nombres") return "nombre";
      if (h === "apellidos") return "apellido";
      return h;
    });
    dataLines = lines.slice(1);
  }

  const rows = dataLines.map((line, idx) => {
    const fields = line.split(delim).map((s) => s.trim().replace(/^"|"$/g, ""));
    const row = { _line: idx + (headerDetected ? 2 : 1) };
    header.forEach((h, i) => {
      row[h] = fields[i] || "";
    });
    // If no header, the only field is taken as email
    if (!headerDetected && fields.length === 1 && !row.email) {
      row.email = fields[0];
    }
    return row;
  });

  return { rows, headerDetected, delimiter: delim };
}

// ═══════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════
export default function AdminClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("ALL");
  const [editing, setEditing] = useState(null); // null | "new" | clientObj
  const [showEmployees, setShowEmployees] = useState(null); // clientObj
  const [uploadFor, setUploadFor] = useState(null); // clientObj
  const [deleting, setDeleting] = useState(null); // clientObj for delete confirmation
  const [permError, setPermError] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setPermError(false);
    try {
      const [clientsSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, "clients")),
        getDocs(collection(db, "users")),
      ]);
      const clientsList = clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const usersList = usersSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));

      // Compute employeeCount per client
      const counts = {};
      usersList.forEach((u) => {
        if (u.clientId) counts[u.clientId] = (counts[u.clientId] || 0) + 1;
      });
      clientsList.forEach((c) => { c.employeeCount = counts[c.id] || 0; });

      clientsList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setClients(clientsList);
      setUsers(usersList);
    } catch (err) {
      console.error("Error fetching clients/users:", err);
      if (err.code === "permission-denied") setPermError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      if (filterCategory !== "ALL" && c.category !== filterCategory) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (c.name || "").toLowerCase().includes(s) ||
        (c.contactEmail || "").toLowerCase().includes(s) ||
        (c.contactName || "").toLowerCase().includes(s)
      );
    });
  }, [clients, search, filterCategory]);

  const handleSaved = (saved) => {
    setEditing(null);
    fetchAll();
  };

  const handleDelete = async (client) => {
    setDeleting({ ...client, busy: true });
    try {
      // Unassign users from this client
      const assigned = users.filter((u) => u.clientId === client.id);
      const batch = writeBatch(db);
      assigned.forEach((u) => {
        batch.update(doc(db, "users", u.uid), { clientId: null });
      });
      // Delete pending assignments
      const pendingSnap = await getDocs(
        query(collection(db, "pending_assignments"), where("clientId", "==", client.id))
      );
      pendingSnap.docs.forEach((d) => batch.delete(doc(db, "pending_assignments", d.id)));
      await batch.commit();

      await deleteDoc(doc(db, "clients", client.id));
      setDeleting(null);
      fetchAll();
    } catch (err) {
      console.error("Error deleting client:", err);
      setDeleting(null);
    }
  };

  return (
    <div data-testid="admin-clients">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Empresas B2B"
          value={clients.filter((c) => c.category === "B2B").length}
          icon={Building2}
          bg="#E0F2FE"
          color="#0284C7"
          testId="stat-clients-b2b"
        />
        <StatCard
          label="Empresas B2G"
          value={clients.filter((c) => c.category === "B2G").length}
          icon={Building2}
          bg="#F3E8FF"
          color="#9333EA"
          testId="stat-clients-b2g"
        />
        <StatCard
          label="Empleados con empresa"
          value={users.filter((u) => !!u.clientId).length}
          icon={Users}
          bg="#FEF3C7"
          color="#D97706"
          testId="stat-users-employed"
        />
        <StatCard
          label="Usuarios B2C"
          value={users.filter((u) => !u.clientId).length}
          icon={Users}
          bg="#DCFCE7"
          color="#16A34A"
          testId="stat-users-b2c"
        />
      </div>

      {/* Search + Filter + New */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
          <input
            type="text"
            placeholder="Buscar por nombre, contacto o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            data-testid="clients-search-input"
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2.5 rounded-lg border text-sm cursor-pointer"
          style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
          data-testid="clients-category-filter"
        >
          <option value="ALL">Todas las categorías</option>
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>

        <button
          onClick={fetchAll}
          className="p-2.5 rounded-lg border transition-colors hover:bg-gray-50"
          style={{ borderColor: "#E2E8F0" }}
          data-testid="clients-refresh-btn"
        >
          <RefreshCw size={16} style={{ color: "#64748B" }} />
        </button>

        <button
          onClick={() => setEditing("new")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-opacity hover:opacity-90"
          style={{ background: "#0284C7", color: "white", border: "none" }}
          data-testid="clients-new-btn"
        >
          <Plus size={16} />
          Nuevo cliente
        </button>
      </div>

      {/* Permission error banner */}
      {permError && (
        <div className="mb-4 p-4 rounded-xl border text-sm" style={{ background: "#FEF3C7", borderColor: "#FCD34D", color: "#92400E" }} data-testid="clients-perm-error">
          <p className="font-semibold mb-1">Permisos insuficientes en Firestore</p>
          <p>Actualiza las reglas para permitir lectura/escritura de la colección <code>clients</code>.</p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin" style={{ color: "#00628F" }} />
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Cliente</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Categoría</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>País</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Contacto</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Empleados</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Estado</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Creado</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const cs = categoryStyle(c.category);
                  const active = c.isActive !== false;
                  return (
                    <tr key={c.id} className="border-t hover:bg-gray-50/50" style={{ borderColor: "#F1F5F9" }} data-testid={`client-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: cs.bg }}>
                            <Building2 size={14} style={{ color: cs.color }} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold truncate notranslate" translate="no" style={{ color: "#1E293B" }}>{c.name || "—"}</div>
                            {c.legalId && (
                              <div className="text-[11px] notranslate" translate="no" style={{ color: "#94A3B8" }}>{c.legalId}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center text-xs font-bold px-2 py-1 rounded-full notranslate" translate="no" style={{ background: cs.bg, color: cs.color }}>
                          {cs.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 notranslate" translate="no" style={{ color: "#64748B" }}>{c.country || "—"}</td>
                      <td className="px-4 py-3">
                        <div style={{ color: "#1E293B" }}>{c.contactName || "—"}</div>
                        <div className="text-[11px]" style={{ color: "#94A3B8" }}>{c.contactEmail || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setShowEmployees(c)}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full cursor-pointer transition-colors hover:opacity-80"
                          style={{ background: "#F1F5F9", color: "#475569", border: "none" }}
                          data-testid={`client-employees-${c.id}`}
                        >
                          <Users size={11} />
                          {c.employeeCount}
                          <ChevronRight size={11} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full" style={active ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEE2E2", color: "#991B1B" }}>
                          {active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3" style={{ color: "#64748B" }}>{fmtDate(c.createdAt)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => navigate(`/cliente?clientId=${c.id}`)}
                            title="Ver Dashboard RRHH / Analítica de Salud"
                            className="p-1.5 rounded-md hover:bg-teal-50 cursor-pointer"
                            style={{ border: "none", background: "none" }}
                            data-testid={`client-dashboard-${c.id}`}
                          >
                            <BarChart3 size={15} style={{ color: "#0D9488" }} />
                          </button>
                          <button
                            onClick={() => setUploadFor(c)}
                            title="Cargar empleados (CSV)"
                            className="p-1.5 rounded-md hover:bg-blue-50 cursor-pointer"
                            style={{ border: "none", background: "none" }}
                            data-testid={`client-upload-${c.id}`}
                          >
                            <Upload size={15} style={{ color: "#0284C7" }} />
                          </button>
                          <button
                            onClick={() => setEditing(c)}
                            title="Editar cliente"
                            className="p-1.5 rounded-md hover:bg-gray-100 cursor-pointer"
                            style={{ border: "none", background: "none" }}
                            data-testid={`client-edit-${c.id}`}
                          >
                            <Edit3 size={15} style={{ color: "#475569" }} />
                          </button>
                          <button
                            onClick={() => setDeleting(c)}
                            title="Eliminar cliente"
                            className="p-1.5 rounded-md hover:bg-red-50 cursor-pointer"
                            style={{ border: "none", background: "none" }}
                            data-testid={`client-delete-${c.id}`}
                          >
                            <Trash2 size={15} style={{ color: "#DC2626" }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12" style={{ color: "#94A3B8" }}>
                      {search || filterCategory !== "ALL"
                        ? "No se encontraron clientes con los filtros actuales"
                        : "Aún no hay clientes registrados. Crea el primero con el botón \"Nuevo cliente\"."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {editing && (
        <ClientFormModal
          client={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
      {showEmployees && (
        <EmployeesModal
          client={showEmployees}
          users={users.filter((u) => u.clientId === showEmployees.id)}
          allUsers={users}
          onClose={() => setShowEmployees(null)}
          onUnassigned={fetchAll}
          onUpload={() => { setUploadFor(showEmployees); setShowEmployees(null); }}
        />
      )}
      {uploadFor && (
        <UploadEmployeesModal
          client={uploadFor}
          users={users}
          clients={clients}
          onClose={() => setUploadFor(null)}
          onCompleted={() => { setUploadFor(null); fetchAll(); }}
        />
      )}
      {deleting && !deleting.busy && (
        <ConfirmDeleteModal
          client={deleting}
          assignedCount={users.filter((u) => u.clientId === deleting.id).length}
          onCancel={() => setDeleting(null)}
          onConfirm={() => handleDelete(deleting)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════

function StatCard({ label, value, icon: Icon, bg, color, testId }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: bg }}>
          <Icon size={20} style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>{value}</p>
          <p className="text-xs" style={{ color: "#64748B" }}>{label}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Client Form Modal ─────────────────────────────────────
function ClientFormModal({ client, onClose, onSaved }) {
  const isNew = !client;
  const [form, setForm] = useState({
    name: client?.name || "",
    category: client?.category || "B2B",
    country: client?.country || "PA",
    legalId: client?.legalId || "",
    contactName: client?.contactName || "",
    contactEmail: client?.contactEmail || "",
    contactPhone: client?.contactPhone || "",
    notes: client?.notes || "",
    emailDomains: (client?.emailDomains || []).join(", "),
    isActive: client?.isActive !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError(null);
    if (!form.name?.trim()) { setError("El nombre del cliente es obligatorio"); return; }
    if (!form.category) { setError("Selecciona una categoría"); return; }
    if (form.contactEmail && !EMAIL_RE.test(form.contactEmail.trim())) {
      setError("El email de contacto no es válido");
      return;
    }
    // Parse domains: "empresa.com, otro.com" -> ["empresa.com", "otro.com"]
    const domains = form.emailDomains
      .split(/[,;\s]+/)
      .map((d) => d.trim().toLowerCase().replace(/^@/, ""))
      .filter((d) => d.length > 0);

    // Dominio corporativo OBLIGATORIO
    if (domains.length === 0) {
      setError("Debes ingresar al menos un dominio corporativo (ej: tuempresa.com)");
      return;
    }

    // Rechazar dominios genéricos (gmail, outlook, etc.)
    const genericFound = domains.filter((d) => !isCorporateDomain(d));
    if (genericFound.length > 0) {
      setError(
        `No se permiten dominios genéricos: ${genericFound.join(", ")}. Solo dominios corporativos.`
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        emailDomains: domains,
        updatedAt: new Date().toISOString(),
      };
      delete payload.emailDomains; // remove the string version before re-adding the array
      payload.emailDomains = domains;

      if (isNew) {
        const newId = `cli_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await setDoc(doc(db, "clients", newId), {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      } else {
        await updateDoc(doc(db, "clients", client.id), payload);
      }
      onSaved();
    } catch (err) {
      console.error("Error saving client:", err);
      setError(err.message || "Error al guardar");
    }
    setSaving(false);
  };

  return (
    <ModalShell title={isNew ? "Nuevo cliente" : "Editar cliente"} onClose={onClose} testId="client-form-modal">
      <div className="space-y-4">
        <Field label="Nombre del cliente *" testId="client-name-input">
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Ej: Walmart Panamá"
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Categoría *" testId="client-category-input">
            <select
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm cursor-pointer"
              style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            >
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </Field>
          <Field label="País" testId="client-country-input">
            <select
              value={form.country}
              onChange={(e) => update("country", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm cursor-pointer"
              style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Identificación legal / RUC / Tax ID" testId="client-legal-id-input">
          <input
            type="text"
            value={form.legalId}
            onChange={(e) => update("legalId", e.target.value)}
            placeholder="Opcional"
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nombre de contacto" testId="client-contact-name-input">
            <input
              type="text"
              value={form.contactName}
              onChange={(e) => update("contactName", e.target.value)}
              placeholder="Persona de referencia"
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
            />
          </Field>
          <Field label="Teléfono" testId="client-contact-phone-input">
            <input
              type="text"
              value={form.contactPhone}
              onChange={(e) => update("contactPhone", e.target.value)}
              placeholder="+507 ..."
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
            />
          </Field>
        </div>

        <Field label="Email de contacto" testId="client-contact-email-input">
          <input
            type="email"
            value={form.contactEmail}
            onChange={(e) => update("contactEmail", e.target.value)}
            placeholder="contacto@empresa.com"
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
          />
        </Field>

        <Field label="Notas internas" testId="client-notes-input">
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Acuerdos, condiciones especiales, etc."
            className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
            style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
          />
        </Field>

        <Field label="Dominios de email corporativos *" testId="client-email-domains-input">
          <input
            type="text"
            value={form.emailDomains}
            onChange={(e) => update("emailDomains", e.target.value)}
            placeholder="tuempresa.com, subsidiaria.com"
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: form.emailDomains.trim() ? "#E2E8F0" : "#FCA5A5", color: "#1E293B" }}
          />
          <p className="text-[10px] mt-1" style={{ color: "#94A3B8" }}>
            Obligatorio. Solo dominios corporativos (no gmail, outlook, etc.). El admin del cliente únicamente verá empleados con email de estos dominios.
          </p>
        </Field>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => update("isActive", e.target.checked)}
            className="w-4 h-4 rounded cursor-pointer"
            style={{ accentColor: "#0284C7" }}
            data-testid="client-active-input"
          />
          <span className="text-sm" style={{ color: "#1E293B" }}>Cliente activo</span>
        </label>

        {error && (
          <div className="p-3 rounded-lg text-sm" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="client-form-error">
            {error}
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-5 justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#F1F5F9", color: "#475569", border: "none" }}
          data-testid="client-form-cancel"
        >
          Cancelar
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-2"
          style={{ background: "#0284C7", color: "white", border: "none", opacity: saving ? 0.6 : 1 }}
          data-testid="client-form-save"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          {isNew ? "Crear cliente" : "Guardar cambios"}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Employees Modal ───────────────────────────────────────
function EmployeesModal({ client, users, allUsers, onClose, onUnassigned, onUpload }) {
  const [unassigning, setUnassigning] = useState(null);
  const [showAssociate, setShowAssociate] = useState(false);
  const [showAssociateProfessional, setShowAssociateProfessional] = useState(false);

  const handleUnassign = async (uid) => {
    if (!confirm("¿Quitar este usuario del cliente? Su perfil personal se mantiene.")) return;
    setUnassigning(uid);
    try {
      await updateDoc(doc(db, "users", uid), { clientId: null, clientRole: "member" });
      onUnassigned();
    } catch (err) { console.error(err); }
    setUnassigning(null);
  };

  const toggleAdmin = async (uid, currentRole) => {
    setUnassigning(uid);
    try {
      const newRole = currentRole === "admin" ? "member" : "admin";
      await updateDoc(doc(db, "users", uid), { clientRole: newRole });
      onUnassigned();
    } catch (err) { console.error(err); }
    setUnassigning(null);
  };

  return (
    <ModalShell
      title={`Empleados de ${client.name}`}
      subtitle={`${users.length} ${users.length === 1 ? "empleado asignado" : "empleados asignados"}`}
      onClose={onClose}
      testId="client-employees-modal"
      wide
    >
      <div className="flex justify-end gap-2 mb-3 flex-wrap">
        <button
          onClick={() => setShowAssociateProfessional(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#F1F5F9", color: "#0D9488", border: "1px solid #0D9488" }}
          data-testid="employees-associate-professional-btn"
        >
          <Stethoscope size={14} />
          Asociar Médico/IHO Externo
        </button>
        <button
          onClick={() => setShowAssociate(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#F1F5F9", color: "#0284C7", border: "1px solid #0284C7" }}
          data-testid="employees-associate-btn"
        >
          <UserPlus size={14} />
          Asociar usuarios existentes
        </button>
        <button
          onClick={onUpload}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#0284C7", color: "white", border: "none" }}
          data-testid="employees-upload-btn"
        >
          <Upload size={14} />
          Cargar empleados (CSV)
        </button>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-8" style={{ color: "#94A3B8" }}>
          <Users size={32} className="mx-auto mb-2" />
          <p className="text-sm">Aún no hay empleados asignados a este cliente.</p>
          <p className="text-xs mt-1">Asocia usuarios ya creados o carga un CSV con varios a la vez.</p>
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[420px] border rounded-lg" style={{ borderColor: "#E2E8F0" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "#F8FAFC" }}>
              <tr>
                <th className="text-left px-3 py-2 font-semibold" style={{ color: "#475569" }}>Email</th>
                <th className="text-left px-3 py-2 font-semibold" style={{ color: "#475569" }}>Nombre</th>
                <th className="text-center px-3 py-2 font-semibold" style={{ color: "#475569" }}>Rol</th>
                <th className="text-center px-3 py-2 font-semibold" style={{ color: "#475569" }}>Estado</th>
                <th className="text-center px-3 py-2 font-semibold w-28" style={{ color: "#475569" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const active = u.isActive !== false;
                const isClientAdmin = u.clientRole === "admin";
                return (
                  <tr key={u.uid} className="border-t" style={{ borderColor: "#F1F5F9" }}>
                    <td className="px-3 py-2" style={{ color: "#1E293B" }}>{u.email || u.profile?.email || "—"}</td>
                    <td className="px-3 py-2" style={{ color: "#475569" }}>
                      {u.displayName || u.profile?.name || "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isClientAdmin ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`emp-role-admin-${u.uid}`}>
                          Admin
                        </span>
                      ) : (
                        <span className="text-[10px]" style={{ color: "#94A3B8" }}>Empleado</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={active ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEE2E2", color: "#991B1B" }}>
                        {active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => toggleAdmin(u.uid, u.clientRole)}
                          disabled={unassigning === u.uid}
                          title={isClientAdmin ? "Quitar como admin del cliente" : "Hacer admin del cliente"}
                          className="p-1 rounded hover:bg-yellow-50 cursor-pointer"
                          style={{ border: "none", background: "none" }}
                          data-testid={`emp-toggle-admin-${u.uid}`}
                        >
                          {unassigning === u.uid
                            ? <Loader2 size={13} className="animate-spin" style={{ color: "#94A3B8" }} />
                            : isClientAdmin
                              ? <ShieldOff size={13} style={{ color: "#D97706" }} />
                              : <ShieldCheck size={13} style={{ color: "#94A3B8" }} />}
                        </button>
                        <button
                          onClick={() => handleUnassign(u.uid)}
                          disabled={unassigning === u.uid}
                          title="Quitar del cliente"
                          className="p-1 rounded hover:bg-red-50 cursor-pointer"
                          style={{ border: "none", background: "none" }}
                          data-testid={`unassign-${u.uid}`}
                        >
                          <X size={13} style={{ color: "#DC2626" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAssociate && (
        <AssociateUsersModal
          client={client}
          allUsers={allUsers}
          onClose={() => setShowAssociate(false)}
          onCompleted={() => { setShowAssociate(false); onUnassigned(); }}
        />
      )}

      {showAssociateProfessional && (
        <AssociateUsersModal
          client={client}
          allUsers={allUsers}
          professionalsOnly={true}
          onClose={() => setShowAssociateProfessional(false)}
          onCompleted={() => { setShowAssociateProfessional(false); onUnassigned(); }}
        />
      )}
    </ModalShell>
  );
}

// ─── Associate Existing Users Modal ────────────────────────
function AssociateUsersModal({ client, allUsers, onClose, onCompleted, professionalsOnly = false }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [includeAssigned, setIncludeAssigned] = useState(false);

  // Pool: B2C users (no clientId) by default; toggle to include users assigned elsewhere
  const pool = useMemo(() => {
    let list = allUsers.filter((u) => {
      if (u.clientId === client.id) return false; // already in this client
      if (!includeAssigned && u.clientId) return false; // hide users with another client
      return true;
    });
    if (professionalsOnly) {
      list = list.filter((u) => u.isMedico === true || u.role === "medico" || u.isIHO === true || u.role === "iho");
    }
    return list;
  }, [allUsers, client.id, includeAssigned, professionalsOnly]);

  const filtered = useMemo(() => {
    if (!search) return pool;
    const s = search.toLowerCase();
    return pool.filter((u) => {
      const email = (u.email || u.profile?.email || "").toLowerCase();
      const name = (u.displayName || u.profile?.name || "").toLowerCase();
      return email.includes(s) || name.includes(s);
    });
  }, [pool, search]);

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((u) => u.uid)));
    }
  };

  const confirm = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const batch = writeBatch(db);
      Array.from(selected).forEach((uid) => {
        batch.update(doc(db, "users", uid), { clientId: client.id });
      });
      await batch.commit();
      onCompleted();
    } catch (err) {
      console.error("Error associating users:", err);
      setError(err.message || "Error al asociar usuarios");
    }
    setBusy(false);
  };

  return (
    <ModalShell
      title={professionalsOnly ? `Asociar Profesionales Externos a ${client.name}` : `Asociar usuarios a ${client.name}`}
      subtitle={professionalsOnly ? "Solo se muestran usuarios registrados con rol de Médico o Oficial de Salud (IHO) en el sistema." : "Selecciona los usuarios que ya existen y deben pertenecer a este cliente"}
      onClose={onClose}
      testId="associate-users-modal"
      wide
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
          <input
            type="text"
            placeholder="Buscar por email o nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            data-testid="associate-search"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: "#475569" }}>
          <input
            type="checkbox"
            checked={includeAssigned}
            onChange={(e) => setIncludeAssigned(e.target.checked)}
            className="w-3.5 h-3.5 rounded cursor-pointer"
            style={{ accentColor: "#0284C7" }}
            data-testid="associate-include-assigned"
          />
          Incluir usuarios ya asignados a otra empresa
        </label>
      </div>

      <div className="border rounded-lg overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        <div className="px-3 py-2 flex items-center justify-between" style={{ background: "#F8FAFC" }}>
          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold" style={{ color: "#475569" }}>
            <input
              type="checkbox"
              checked={filtered.length > 0 && selected.size === filtered.length}
              onChange={toggleAll}
              className="w-3.5 h-3.5 rounded cursor-pointer"
              style={{ accentColor: "#0284C7" }}
              data-testid="associate-select-all"
            />
            {filtered.length === 0
              ? "Sin usuarios"
              : selected.size === filtered.length
                ? `Deseleccionar todos (${filtered.length})`
                : `Seleccionar todos (${filtered.length})`}
          </label>
          <span className="text-xs font-semibold" style={{ color: "#0284C7" }}>
            {selected.size} seleccionados
          </span>
        </div>

        <div className="overflow-y-auto max-h-[360px]">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: "#94A3B8" }}>
              {includeAssigned
                ? "No hay usuarios disponibles para asociar."
                : "No hay usuarios B2C disponibles. Marca la opción de arriba para incluir usuarios asignados a otra empresa."}
            </div>
          ) : (
            filtered.map((u) => {
              const email = u.email || u.profile?.email || "";
              const name = u.displayName || u.profile?.name || "";
              const otherClient = u.clientId
                ? allUsers.find((au) => au.clientId === u.clientId) // dummy
                : null;
              // Better: lookup client name passed via allClients? we don't have it here
              // We'll just show "Otra empresa" badge when applicable
              return (
                <label
                  key={u.uid}
                  className="flex items-center gap-3 px-3 py-2 border-t cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: "#F1F5F9" }}
                  data-testid={`associate-row-${u.uid}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.uid)}
                    onChange={() => toggle(u.uid)}
                    className="w-4 h-4 rounded cursor-pointer"
                    style={{ accentColor: "#0284C7" }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" style={{ color: "#1E293B" }}>
                      {email || "(sin email)"}
                    </div>
                    {name && (
                      <div className="text-[11px] truncate" style={{ color: "#94A3B8" }}>{name}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {u.isIHO && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}>
                        IHO
                      </span>
                    )}
                    {(u.isMedico || u.role === "medico") && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#ECFDF5", color: "#0D9488", border: "1px solid #A7F3D0" }}>
                        Médico
                      </span>
                    )}
                    {u.clientId ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                        Otra empresa
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>
                        B2C
                      </span>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 p-3 rounded-lg text-sm" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="associate-error">
          {error}
        </div>
      )}

      <div className="flex gap-2 mt-4 justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#F1F5F9", color: "#475569", border: "none" }}
          data-testid="associate-cancel"
        >
          Cancelar
        </button>
        <button
          onClick={confirm}
          disabled={busy || selected.size === 0}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-2"
          style={{
            background: "#0284C7",
            color: "white",
            border: "none",
            opacity: busy || selected.size === 0 ? 0.5 : 1,
            cursor: busy || selected.size === 0 ? "not-allowed" : "pointer",
          }}
          data-testid="associate-confirm"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Asociar {selected.size > 0 ? selected.size : ""} {selected.size === 1 ? "usuario" : "usuarios"}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Upload Employees Modal ────────────────────────────────
function UploadEmployeesModal({ client, users, clients, onClose, onCompleted }) {
  const [step, setStep] = useState("upload"); // upload | preview | done
  const [parsed, setParsed] = useState(null); // { rows, classified }
  const [conflictDecisions, setConflictDecisions] = useState({}); // email -> "skip" | "reassign"
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const usersByEmail = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      const e = (u.email || u.profile?.email || "").toLowerCase().trim();
      if (e) map.set(e, u);
    });
    return map;
  }, [users]);

  const clientNameById = useMemo(() => {
    const map = new Map();
    clients.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [clients]);

  const onFile = async (file) => {
    setError(null);
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError("Archivo demasiado grande (máx 2MB)"); return; }
    try {
      const text = await file.text();
      const { rows } = parseCsv(text);
      const classified = {
        valid: [],         // new: will create pending_assignment
        existingFree: [],  // user exists with no clientId → reassign directly
        sameClient: [],    // user already assigned to THIS client → skip
        conflicts: [],     // user assigned to OTHER client → ask admin
        invalid: [],       // bad email or missing
      };
      const seenEmails = new Set();
      rows.forEach((r) => {
        const email = (r.email || "").toLowerCase().trim();
        if (!email || !EMAIL_RE.test(email)) {
          classified.invalid.push({ ...r, email, reason: !email ? "Email vacío" : "Email inválido" });
          return;
        }
        if (seenEmails.has(email)) {
          classified.invalid.push({ ...r, email, reason: "Duplicado en el archivo" });
          return;
        }
        seenEmails.add(email);
        const existing = usersByEmail.get(email);
        if (existing) {
          if (existing.clientId === client.id) {
            classified.sameClient.push({ ...r, email, user: existing });
          } else if (existing.clientId) {
            classified.conflicts.push({
              ...r,
              email,
              user: existing,
              currentClientId: existing.clientId,
              currentClientName: clientNameById.get(existing.clientId) || "Cliente desconocido",
            });
          } else {
            classified.existingFree.push({ ...r, email, user: existing });
          }
        } else {
          classified.valid.push({ ...r, email });
        }
      });
      setParsed({ rows, classified });
      // Default decisions for conflicts: skip
      const defaults = {};
      classified.conflicts.forEach((c) => { defaults[c.email] = "skip"; });
      setConflictDecisions(defaults);
      setStep("preview");
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo leer el archivo");
    }
  };

  const confirm = async () => {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    const { classified } = parsed;
    const summary = { created: 0, assigned: 0, reassigned: 0, skipped: 0, invalid: classified.invalid.length };
    try {
      const batch = writeBatch(db);

      // 1. Pending assignments for emails without account
      classified.valid.forEach((r) => {
        const ref = doc(db, "pending_assignments", r.email);
        batch.set(ref, {
          email: r.email,
          clientId: client.id,
          clientName: client.name,
          firstName: r.nombre || "",
          lastName: r.apellido || "",
          createdAt: new Date().toISOString(),
        });
        summary.created++;
      });

      // 2. Existing users without clientId → assign directly
      classified.existingFree.forEach((r) => {
        batch.update(doc(db, "users", r.user.uid), { clientId: client.id });
        summary.assigned++;
      });

      // 3. Conflicts → handle per decision
      classified.conflicts.forEach((r) => {
        const decision = conflictDecisions[r.email] || "skip";
        if (decision === "reassign") {
          batch.update(doc(db, "users", r.user.uid), { clientId: client.id });
          summary.reassigned++;
        } else {
          summary.skipped++;
        }
      });

      // 4. sameClient → skip silently
      summary.skipped += classified.sameClient.length;

      await batch.commit();
      setResult(summary);
      setStep("done");
    } catch (err) {
      console.error("Error in batch upload:", err);
      setError(err.message || "Error al guardar las asignaciones");
    }
    setBusy(false);
  };

  const isStepUpload = step === "upload";
  const isStepPreview = step === "preview";
  const isStepDone = step === "done";

  return (
    <ModalShell
      title={`Cargar empleados a ${client.name}`}
      subtitle="Sube un archivo CSV con las columnas: email, nombre, apellido"
      onClose={onClose}
      testId="upload-employees-modal"
      wide
    >
      {isStepUpload && (
        <div data-testid="upload-step-1">
          <div
            className="border-2 border-dashed rounded-xl p-8 text-center"
            style={{ borderColor: "#CBD5E1", background: "#F8FAFC" }}
          >
            <FileText size={32} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
            <p className="text-sm font-semibold mb-1" style={{ color: "#1E293B" }}>
              Selecciona un archivo CSV
            </p>
            <p className="text-xs mb-4" style={{ color: "#64748B" }}>
              Formato esperado: <code>email,nombre,apellido</code> (mínimo email)
            </p>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="text-sm"
              data-testid="upload-csv-input"
            />
          </div>
          <details className="mt-4 text-xs" style={{ color: "#64748B" }}>
            <summary className="cursor-pointer font-semibold">Ver ejemplo de CSV</summary>
            <pre className="mt-2 p-3 rounded-lg" style={{ background: "#F1F5F9", color: "#1E293B" }}>{`email,nombre,apellido
ana.lopez@empresa.com,Ana,López
jose.perez@empresa.com,José,Pérez
maria@empresa.com,María,`}</pre>
          </details>
          {error && (
            <div className="mt-3 p-3 rounded-lg text-sm" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="upload-error">
              {error}
            </div>
          )}
        </div>
      )}

      {isStepPreview && parsed && (
        <div data-testid="upload-step-2">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            <PreviewStat label="Nuevos" value={parsed.classified.valid.length} color="#16A34A" testId="preview-stat-new" />
            <PreviewStat label="Asignar" value={parsed.classified.existingFree.length} color="#0284C7" testId="preview-stat-assign" />
            <PreviewStat label="Conflictos" value={parsed.classified.conflicts.length} color="#F9A01B" testId="preview-stat-conflict" />
            <PreviewStat label="Ya en cliente" value={parsed.classified.sameClient.length} color="#94A3B8" testId="preview-stat-same" />
            <PreviewStat label="Inválidos" value={parsed.classified.invalid.length} color="#C0453A" testId="preview-stat-invalid" />
          </div>

          <div className="overflow-y-auto max-h-[360px] border rounded-lg" style={{ borderColor: "#E2E8F0" }}>
            {parsed.classified.conflicts.length > 0 && (
              <Section title="Conflictos — decide qué hacer" color="#F9A01B" icon={AlertTriangle}>
                {parsed.classified.conflicts.map((c) => (
                  <ConflictRow
                    key={c.email}
                    item={c}
                    decision={conflictDecisions[c.email] || "skip"}
                    onChange={(d) => setConflictDecisions((prev) => ({ ...prev, [c.email]: d }))}
                  />
                ))}
              </Section>
            )}

            {parsed.classified.valid.length > 0 && (
              <Section title="Cuentas nuevas (se crearán al primer login)" color="#16A34A" icon={CheckCircle2}>
                {parsed.classified.valid.map((r) => (
                  <SimpleRow key={r.email} email={r.email} extra={`${r.nombre || ""} ${r.apellido || ""}`.trim()} />
                ))}
              </Section>
            )}

            {parsed.classified.existingFree.length > 0 && (
              <Section title="Usuarios existentes (se asignarán)" color="#0284C7" icon={CheckCircle2}>
                {parsed.classified.existingFree.map((r) => (
                  <SimpleRow key={r.email} email={r.email} extra={r.user.displayName || ""} />
                ))}
              </Section>
            )}

            {parsed.classified.sameClient.length > 0 && (
              <Section title="Ya en este cliente (se ignorarán)" color="#94A3B8" icon={CheckCircle2}>
                {parsed.classified.sameClient.map((r) => (
                  <SimpleRow key={r.email} email={r.email} extra={r.user.displayName || ""} />
                ))}
              </Section>
            )}

            {parsed.classified.invalid.length > 0 && (
              <Section title="Inválidos (se ignorarán)" color="#C0453A" icon={AlertTriangle}>
                {parsed.classified.invalid.map((r, i) => (
                  <SimpleRow key={`${r.email}-${i}`} email={r.email || "(vacío)"} extra={r.reason} />
                ))}
              </Section>
            )}
          </div>

          {error && (
            <div className="mt-3 p-3 rounded-lg text-sm" style={{ background: "#FEE2E2", color: "#991B1B" }}>
              {error}
            </div>
          )}

          <div className="flex gap-2 mt-4 justify-end">
            <button
              onClick={() => { setParsed(null); setStep("upload"); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer"
              style={{ background: "#F1F5F9", color: "#475569", border: "none" }}
              data-testid="upload-back-btn"
            >
              Atrás
            </button>
            <button
              onClick={confirm}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-2"
              style={{ background: "#0284C7", color: "white", border: "none", opacity: busy ? 0.6 : 1 }}
              data-testid="upload-confirm-btn"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Confirmar carga
            </button>
          </div>
        </div>
      )}

      {isStepDone && result && (
        <div className="text-center py-6" data-testid="upload-step-3">
          <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: "#16A34A" }} />
          <h3 className="text-lg font-bold mb-2" style={{ color: "#1E293B" }}>Carga completada</h3>
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mt-4 text-left text-sm">
            <ResultRow label="Cuentas pendientes creadas" value={result.created} color="#16A34A" />
            <ResultRow label="Usuarios asignados" value={result.assigned} color="#0284C7" />
            <ResultRow label="Reasignados" value={result.reassigned} color="#F9A01B" />
            <ResultRow label="Ignorados" value={result.skipped} color="#94A3B8" />
          </div>
          <button
            onClick={onCompleted}
            className="mt-6 px-5 py-2 rounded-lg text-sm font-semibold cursor-pointer"
            style={{ background: "#0284C7", color: "white", border: "none" }}
            data-testid="upload-done-btn"
          >
            Cerrar
          </button>
        </div>
      )}
    </ModalShell>
  );
}

function ConflictRow({ item, decision, onChange }) {
  return (
    <div className="px-3 py-2 border-t flex items-center justify-between gap-3" style={{ borderColor: "#F1F5F9" }} data-testid={`conflict-${item.email}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate" style={{ color: "#1E293B" }}>{item.email}</div>
        <div className="text-[11px]" style={{ color: "#94A3B8" }}>
          Actualmente en: <strong>{item.currentClientName}</strong>
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button
          onClick={() => onChange("skip")}
          className="text-[11px] px-2 py-1 rounded font-semibold cursor-pointer"
          style={decision === "skip"
            ? { background: "#94A3B8", color: "white", border: "none" }
            : { background: "#F1F5F9", color: "#64748B", border: "none" }
          }
          data-testid={`conflict-${item.email}-skip`}
        >
          Mantener actual
        </button>
        <button
          onClick={() => onChange("reassign")}
          className="text-[11px] px-2 py-1 rounded font-semibold cursor-pointer"
          style={decision === "reassign"
            ? { background: "#F9A01B", color: "white", border: "none" }
            : { background: "#F1F5F9", color: "#64748B", border: "none" }
          }
          data-testid={`conflict-${item.email}-reassign`}
        >
          Reasignar
        </button>
      </div>
    </div>
  );
}

function Section({ title, color, icon: Icon, children }) {
  return (
    <div>
      <div className="px-3 py-2 flex items-center gap-2" style={{ background: "#F8FAFC", borderTop: "1px solid #E2E8F0" }}>
        <Icon size={13} style={{ color }} />
        <span className="text-xs font-bold" style={{ color: "#1E293B" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function SimpleRow({ email, extra }) {
  return (
    <div className="px-3 py-2 border-t flex items-center justify-between" style={{ borderColor: "#F1F5F9" }}>
      <span className="text-sm" style={{ color: "#1E293B" }}>{email}</span>
      {extra && <span className="text-[11px]" style={{ color: "#94A3B8" }}>{extra}</span>}
    </div>
  );
}

function PreviewStat({ label, value, color, testId }) {
  return (
    <div
      className="rounded-lg p-2 text-center"
      style={{ background: `${color}15` }}
      data-testid={testId}
    >
      <div className="text-lg font-bold" style={{ color }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide" style={{ color }}>{label}</div>
    </div>
  );
}

function ResultRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between p-2 rounded-lg" style={{ background: `${color}10` }}>
      <span style={{ color: "#475569" }}>{label}</span>
      <strong style={{ color }}>{value}</strong>
    </div>
  );
}

// ─── Confirm Delete Modal ──────────────────────────────────
function ConfirmDeleteModal({ client, assignedCount, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <ModalShell title="Eliminar cliente" onClose={onCancel} testId="client-delete-modal">
      <div className="text-sm mb-4" style={{ color: "#1E293B" }}>
        ¿Seguro que quieres eliminar el cliente <strong>{client.name}</strong>?
      </div>
      {assignedCount > 0 && (
        <div className="p-3 rounded-lg text-sm mb-4" style={{ background: "#FEF3C7", color: "#92400E" }}>
          <AlertTriangle size={14} className="inline mr-1" />
          {assignedCount} {assignedCount === 1 ? "usuario quedará" : "usuarios quedarán"} sin cliente asignado. Sus perfiles personales se conservan.
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer"
          style={{ background: "#F1F5F9", color: "#475569", border: "none" }}
          data-testid="client-delete-cancel"
        >
          Cancelar
        </button>
        <button
          onClick={async () => { setBusy(true); await onConfirm(); }}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-2"
          style={{ background: "#DC2626", color: "white", border: "none", opacity: busy ? 0.6 : 1 }}
          data-testid="client-delete-confirm"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Eliminar definitivamente
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Modal Shell ───────────────────────────────────────────
function ModalShell({ title, subtitle, onClose, children, testId, wide }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid={testId}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden"
        style={{ maxWidth: wide ? 720 : 480, maxHeight: "92vh", display: "flex", flexDirection: "column" }}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between shrink-0" style={{ borderColor: "#E2E8F0" }}>
          <div className="min-w-0">
            <h3 className="text-base font-bold truncate" style={{ color: "#1E293B" }}>{title}</h3>
            {subtitle && <p className="text-xs truncate" style={{ color: "#64748B" }}>{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer shrink-0"
            style={{ border: "none", background: "none" }}
            data-testid={`${testId}-close`}
          >
            <X size={18} style={{ color: "#64748B" }} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto" style={{ flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="block text-xs font-semibold mb-1.5" style={{ color: "#475569" }}>{label}</label>
      {children}
    </div>
  );
}
